// /admin/usuarios — four tabs. "Equipo": the INHERTA staff roster, searchable
// and server-paginated — who's on the team, their role, MFA + activity. Every
// management action (role change, deactivate/reactivate, password reset, MFA
// reset, session revoke, delete) now lives on the member's own details page
// (UserDetail.tsx) — a row here is navigation-only (name cell -> details).
// "Invitaciones": pending org invites, with their cancel/resend actions
// (invites have no details page to route to, so those actions stay here).
// "Seguridad": the "Seguridad del equipo" MFA-required policy card. The
// direct-create flow ("Agregar usuario") stays in the page header, visible
// across every tab. Composes the DataTable + Modal design system exactly
// like Tenants.tsx / AccountManagers.tsx / Settings.tsx. Data via the
// users-data.ts seam, wired to GET /admin/users (q/limit/offset
// search+pagination), DELETE /admin/users/invites/:id, POST
// /admin/users/invites/:id/resend, POST /admin/users, and GET+PATCH
// /admin/settings/security.
//
// "Directorio" (platform-owner epic, PR 13): a read-only, searchable list of
// EVERY user on the platform — INHERTA staff AND client users — with their
// tenant memberships, for support lookups. Wired to GET /admin/directory via
// searchDirectory() in users-data.ts. Its filter row (cliente / rol INHERTA /
// rol de cliente / estado) narrows SERVER-side, composing with the search;
// the cliente picker reuses the Clientes page's own ['admin', 'tenants'] query.

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { listTenants } from './tenants-data.js'
import { AddUserModal } from './users/AddUserModal.js'
import {
  cancelOrgInvite,
  type DirectoryResult,
  getOrgUsers,
  getSecuritySettings,
  grantableRoles,
  type OrgMember,
  type PendingInvite,
  PLATFORM_ROLES,
  relativeDateEs,
  resendOrgInvite,
  ROLE_BADGE,
  searchDirectory,
  setMfaRequired,
  TENANT_ROLES,
} from './users-data.js'
import { useSession } from '../../auth/session.js'
import {
  Avatar,
  Button,
  Card,
  Chip,
  DataTable,
  type DataTableColumn,
  FilterBar,
  FilterButton,
  Modal,
  PageHeader,
  SearchInput,
  Select,
  Tabs,
  TabPanel,
  toast,
  ToggleSwitch,
} from '../../design-system/index.js'
import { apiErrorMessage } from '../../lib/api-error.js'
import { useAvatar } from '../../lib/useAvatar.js'

const DIRECTORY_PAGE_SIZE = 25
const EQUIPO_PAGE_SIZE = 25

/** Row-leading avatar for a directory/member row: fetches the photo (gated on
 *  has_avatar so a person without one never fires the GET) and falls back to
 *  initials via the DS Avatar. */
function MemberAvatar({ id, name, hasAvatar }: { id: string; name: string; hasAvatar: boolean }) {
  const src = useAvatar(id, hasAvatar)
  return <Avatar name={name} src={src} size={32} hideName />
}

/** Same name-cell-as-button navigation as memberColumns (see its comment) —
 *  a Directorio row also opens the details page, staff or tenant user
 *  alike. The tenant links live in the separate "Clientes" cell, not nested
 *  inside this button, so they need no stopPropagation to "keep working": a
 *  click there never reaches this cell's handler in the first place. */
function directoryColumns(navigate: (path: string) => void): DataTableColumn<DirectoryResult>[] {
  return [
    {
      key: 'name',
      label: 'Nombre',
      render: (r) => (
        <button
          type="button"
          onClick={() => navigate(`/admin/usuarios/${r.id}`)}
          className="flex items-center gap-2.5 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
        >
          <MemberAvatar id={r.id} name={r.name} hasAvatar={r.has_avatar} />
          <div>
            <div className="font-semibold text-text hover:text-link">{r.name}</div>
            <div className="text-[11px] text-text-2">{r.email}</div>
          </div>
        </button>
      ),
    },
    {
      key: 'platform_role',
      label: 'Rol INHERTA',
      render: (r) =>
        r.platform_role ? (
          <Chip tone={ROLE_BADGE[r.platform_role].tone}>{ROLE_BADGE[r.platform_role].label}</Chip>
        ) : (
          <span className="text-text-3">—</span>
        ),
    },
    {
      key: 'active',
      label: 'Estado',
      render: (r) =>
        r.active ? (
          <Chip tone="ok" dot>
            Activa
          </Chip>
        ) : (
          <Chip tone="sens" dot>
            Desactivada
          </Chip>
        ),
    },
    {
      key: 'tenants',
      label: 'Clientes',
      render: (r) =>
        r.tenants.length === 0 ? (
          <span className="text-text-3">—</span>
        ) : (
          <div className="flex flex-col gap-1">
            {r.tenants.map((t) => (
              <div key={t.tenant_id} className="flex items-center gap-1.5">
                <Link
                  to={`/admin/tenants/${t.tenant_id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="text-link hover:underline"
                >
                  {t.tenant_name}
                </Link>
                <span className="text-[11px] text-text-2">{t.role}</span>
              </div>
            ))}
          </div>
        ),
    },
  ]
}

const PLATFORM_ROLE_OPTIONS = [
  { value: '', label: 'Todos los roles INHERTA' },
  ...PLATFORM_ROLES.map((r) => ({ value: r.value, label: r.label })),
]
const TENANT_ROLE_OPTIONS = [
  { value: '', label: 'Todos los roles de cliente' },
  ...TENANT_ROLES.map((r) => ({ value: r.value, label: r.label })),
]
const ACTIVE_OPTIONS = [
  { value: '', label: 'Activas y desactivadas' },
  { value: 'true', label: 'Solo activas' },
  { value: 'false', label: 'Solo desactivadas' },
]

/** "Directorio" tab — every user on the platform (INHERTA staff and client
 *  users alike), searchable by name/email and narrowable by cliente, rol and
 *  estado de la cuenta, with their tenant memberships. Every filter is applied
 *  SERVER-side (they compose with q and with each other) and lives in the
 *  query key, so each combination is cached separately.
 *  Read-only: this is a support lookup, not a management surface (that's the
 *  Equipo tab and each tenant's own Equipo panel). */
function DirectoryPanel(): JSX.Element {
  const navigate = useNavigate()
  const [queryInput, setQueryInput] = useState('')
  const [query, setQuery] = useState('')
  const [tenantId, setTenantId] = useState('')
  const [platformRole, setPlatformRole] = useState('')
  const [tenantRole, setTenantRole] = useState('')
  const [active, setActive] = useState('')
  const [offset, setOffset] = useState(0)

  // Debounce: wait 300ms of typing silence before firing the search.
  useEffect(() => {
    const timer = setTimeout(() => setQuery(queryInput.trim()), 300)
    return () => clearTimeout(timer)
  }, [queryInput])

  // A new search always restarts at the first page. The selects reset `offset`
  // in their own onChange (below) rather than through an effect, so a filter
  // change and its page reset land in the same render.
  useEffect(() => {
    setOffset(0)
  }, [query])

  // Same key + fetcher as the Clientes page, so the picker reuses that cache
  // instead of standing up a second tenant-list endpoint for one dropdown.
  const { data: tenants = [] } = useQuery({ queryKey: ['admin', 'tenants'], queryFn: listTenants })
  const tenantOptions = [
    { value: '', label: 'Todos los clientes' },
    ...[...tenants]
      .sort((a, b) => a.name.localeCompare(b.name, 'es'))
      .map((t) => ({ value: t.id, label: t.name })),
  ]

  const filters = {
    tenantId: tenantId === '' ? undefined : tenantId,
    platformRole: platformRole === '' ? undefined : platformRole,
    tenantRole: tenantRole === '' ? undefined : tenantRole,
    active: active === '' ? undefined : active === 'true',
  }
  const hasFilters =
    queryInput.trim() !== '' ||
    tenantId !== '' ||
    platformRole !== '' ||
    tenantRole !== '' ||
    active !== ''

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'directory', query, tenantId, platformRole, tenantRole, active, offset],
    queryFn: () => searchDirectory(query, DIRECTORY_PAGE_SIZE, offset, filters),
  })

  const results = data?.results ?? []

  /** Any filter change restarts at the first page — page 3 of the old result
   *  set is meaningless against the new one. */
  function applyFilter(set: (v: string) => void, value: string): void {
    set(value)
    setOffset(0)
  }

  function clearFilters(): void {
    setQueryInput('')
    setQuery('')
    setTenantId('')
    setPlatformRole('')
    setTenantRole('')
    setActive('')
    setOffset(0)
  }

  return (
    <div className="flex flex-col gap-3.5">
      <FilterBar>
        <SearchInput
          value={queryInput}
          onChange={(e) => setQueryInput(e.target.value)}
          placeholder="Buscar por nombre o correo…"
          aria-label="Buscar en el directorio"
        />
        <div className="w-full sm:w-48">
          <Select
            aria-label="Filtrar por cliente"
            options={tenantOptions}
            value={tenantId}
            onChange={(e) => applyFilter(setTenantId, e.target.value)}
          />
        </div>
        <div className="w-full sm:w-48">
          <Select
            aria-label="Filtrar por rol INHERTA"
            options={PLATFORM_ROLE_OPTIONS}
            value={platformRole}
            onChange={(e) => applyFilter(setPlatformRole, e.target.value)}
          />
        </div>
        <div className="w-full sm:w-48">
          <Select
            aria-label="Filtrar por rol de cliente"
            options={TENANT_ROLE_OPTIONS}
            value={tenantRole}
            onChange={(e) => applyFilter(setTenantRole, e.target.value)}
          />
        </div>
        <div className="w-full sm:w-48">
          <Select
            aria-label="Filtrar por estado de la cuenta"
            options={ACTIVE_OPTIONS}
            value={active}
            onChange={(e) => applyFilter(setActive, e.target.value)}
          />
        </div>
        {hasFilters && <FilterButton onClick={clearFilters}>Limpiar filtros</FilterButton>}
      </FilterBar>
      <DataTable
        caption="Directorio de usuarios"
        columns={directoryColumns((path) => void navigate(path))}
        rows={results}
        loading={isLoading}
        empty={
          <div className="px-5 py-10 text-center text-[12.5px] text-text-2">Sin resultados.</div>
        }
      />
      {(offset > 0 || results.length === DIRECTORY_PAGE_SIZE) && (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - DIRECTORY_PAGE_SIZE))}
          >
            Anterior
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={results.length < DIRECTORY_PAGE_SIZE}
            onClick={() => setOffset(offset + DIRECTORY_PAGE_SIZE)}
          >
            Siguiente
          </Button>
        </div>
      )}
    </div>
  )
}

/** Columns take `navigate` so the Nombre cell can route to the details page
 *  (mirrors Tenants.tsx's name-cell-as-button pattern — DataTable has no
 *  onRowClick, so the whole-row destination lives on the primary cell). A
 *  row carries no actions menu anymore — every management action lives on
 *  that details page instead (role change, deactivate/reactivate, password
 *  reset, MFA reset, session revoke, delete). */
function memberColumns(navigate: (path: string) => void): DataTableColumn<OrgMember>[] {
  return [
    {
      key: 'name',
      label: 'Nombre',
      render: (m) => (
        <button
          type="button"
          onClick={() => navigate(`/admin/usuarios/${m.id}`)}
          className="flex items-center gap-2.5 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
        >
          <MemberAvatar id={m.id} name={m.name} hasAvatar={m.has_avatar} />
          <div>
            <div className="font-semibold text-text hover:text-link">{m.name}</div>
            <div className="text-[11px] text-text-2">{m.email}</div>
          </div>
        </button>
      ),
    },
    {
      key: 'platform_role',
      label: 'Rol',
      render: (m) => (
        <Chip tone={ROLE_BADGE[m.platform_role].tone}>{ROLE_BADGE[m.platform_role].label}</Chip>
      ),
    },
    {
      key: 'mfa_enabled',
      label: 'MFA',
      render: (m) => (
        <Chip tone={m.mfa_enabled ? 'ok' : 'warn'} dot>
          {m.mfa_enabled ? 'Activo' : 'Sin configurar'}
        </Chip>
      ),
    },
    {
      key: 'active',
      label: 'Estado',
      render: (m) =>
        m.active ? (
          <Chip tone="ok" dot>
            Activa
          </Chip>
        ) : (
          <Chip tone="sens" dot>
            Desactivada
          </Chip>
        ),
    },
    {
      key: 'last_login',
      label: 'Último acceso',
      align: 'right',
      render: (m) => <span className="tabular-nums">{relativeDateEs(m.last_login)}</span>,
    },
  ]
}

/** "Equipo" tab — the INHERTA team roster, searchable by name/email with
 *  server-side pagination (mirrors DirectoryPanel's debounce + offset
 *  pattern exactly). Its own query key (['admin', 'org-users', query,
 *  offset]) is a suffix of the plain ['admin', 'org-users'] key every
 *  member-mutating modal invalidates by prefix, so a role change, delete,
 *  etc. on the details page always refreshes whatever page/search is
 *  currently showing here. */
function EquipoPanel({ navigate }: { navigate: (path: string) => void }): JSX.Element {
  const [queryInput, setQueryInput] = useState('')
  const [query, setQuery] = useState('')
  const [offset, setOffset] = useState(0)

  useEffect(() => {
    const timer = setTimeout(() => setQuery(queryInput.trim()), 300)
    return () => clearTimeout(timer)
  }, [queryInput])

  useEffect(() => {
    setOffset(0)
  }, [query])

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'org-users', query, offset],
    queryFn: () => getOrgUsers(query, EQUIPO_PAGE_SIZE, offset),
  })

  const members = data?.members ?? []
  const total = data?.members_total ?? 0
  const hasNext = offset + members.length < total

  return (
    <div className="flex flex-col gap-3.5">
      <FilterBar>
        <SearchInput
          value={queryInput}
          onChange={(e) => setQueryInput(e.target.value)}
          placeholder="Buscar por nombre o correo…"
          aria-label="Buscar en el equipo"
        />
      </FilterBar>
      <DataTable
        caption="Equipo INHERTA"
        columns={memberColumns(navigate)}
        rows={members}
        loading={isLoading}
        empty={
          <div className="px-5 py-10 text-center text-[12.5px] text-text-2">
            {query === '' ? 'Nadie en el equipo todavía.' : 'Sin resultados.'}
          </div>
        }
      />
      {(offset > 0 || hasNext) && (
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - EQUIPO_PAGE_SIZE))}
          >
            Anterior
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={!hasNext}
            onClick={() => setOffset(offset + EQUIPO_PAGE_SIZE)}
          >
            Siguiente
          </Button>
        </div>
      )}
    </div>
  )
}

/** "Seguridad del equipo" — the team-wide MFA-required toggle. Renders
 *  nothing when the GET 403s (a role without settings.security.read): the
 *  card simply doesn't appear rather than showing an error on an admin-only
 *  page. */
function SecurityCard(): JSX.Element | null {
  const qc = useQueryClient()
  const { data, isError } = useQuery({
    queryKey: ['admin', 'security-settings'],
    queryFn: getSecuritySettings,
    retry: false,
  })
  // The value the toggle was flipped TO, pending confirmation; null = no
  // confirm modal open. The switch itself always reflects `data.mfa_required`
  // — flipping it never applies immediately, only opens this modal.
  const [pendingValue, setPendingValue] = useState<boolean | null>(null)
  const [busy, setBusy] = useState(false)

  if (isError || !data) return null

  async function confirm(): Promise<void> {
    if (pendingValue === null) return
    setBusy(true)
    try {
      await setMfaRequired(pendingValue)
      toast.ok(pendingValue ? 'MFA obligatoria activada' : 'MFA obligatoria desactivada')
      setPendingValue(null)
      await qc.invalidateQueries({ queryKey: ['admin', 'security-settings'] })
    } catch (err) {
      toast.error(apiErrorMessage(err, 'No se pudo actualizar la configuración.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <Card.Body>
        <h2 className="text-[13.5px] font-bold text-text">Seguridad del equipo</h2>
        <div className="mt-3 flex items-center justify-between gap-3">
          <span className="text-[12.5px] text-text">MFA obligatoria para el equipo INHERTA</span>
          <ToggleSwitch
            checked={data.mfa_required}
            onChange={(checked) => setPendingValue(checked)}
            label="MFA obligatoria para el equipo INHERTA"
            hideLabel
          />
        </div>
      </Card.Body>

      <Modal
        open={pendingValue !== null}
        onClose={() => setPendingValue(null)}
        role="alertdialog"
        title={pendingValue === true ? 'Hacer obligatoria la MFA' : 'Quitar la MFA obligatoria'}
        subtitle={
          pendingValue === true
            ? data.unenrolled_staff.length > 0
              ? 'Estas personas quedarán bloqueadas de la consola hasta configurar MFA:'
              : 'Todo el equipo ya tiene MFA configurada.'
            : 'El equipo podrá entrar sin MFA y desactivarla.'
        }
        busy={busy}
        footer={
          <>
            <Button variant="secondary" onClick={() => setPendingValue(null)} disabled={busy}>
              Cancelar
            </Button>
            <Button onClick={() => void confirm()} loading={busy}>
              {pendingValue === true ? 'Hacer obligatoria' : 'Quitar obligatoriedad'}
            </Button>
          </>
        }
      >
        {pendingValue === true && data.unenrolled_staff.length > 0 && (
          <ul className="flex flex-col gap-1.5">
            {data.unenrolled_staff.map((s) => (
              <li key={s.id} className="text-[12.5px]">
                <span className="font-semibold text-text">{s.name}</span>{' '}
                <span className="text-text-3">{s.email}</span>
              </li>
            ))}
          </ul>
        )}
      </Modal>
    </Card>
  )
}

export function UsersPage(): JSX.Element {
  // "Invitaciones" tab reads `pending` off the same plain, unfiltered
  // ['admin', 'org-users'] query the mutating modals invalidate by prefix
  // (Equipo's own paginated/searched query is separate, keyed underneath
  // this one — see EquipoPanel). Pending invites are never paginated
  // server-side (they're small), so this single unfiltered fetch is enough.
  const { data } = useQuery({ queryKey: ['admin', 'org-users'], queryFn: () => getOrgUsers() })
  const [tab, setTab] = useState<'equipo' | 'directorio' | 'invitaciones' | 'seguridad'>('equipo')

  const navigate = useNavigate()
  const session = useSession()
  const roles = grantableRoles(session?.platformRole)
  const isOwnerCaller = session?.platformRole === 'platform_owner'

  const [busyId, setBusyId] = useState<string | null>(null)

  // ── consolidated "Agregar usuario" modal ──
  const [addUserOpen, setAddUserOpen] = useState(false)

  const qc = useQueryClient()

  async function refreshMembers(): Promise<void> {
    await qc.invalidateQueries({ queryKey: ['admin', 'org-users'] })
  }

  async function handleResend(id: string): Promise<void> {
    setBusyId(id)
    try {
      await resendOrgInvite(id)
      toast.ok('Invitación reenviada')
      await qc.invalidateQueries({ queryKey: ['admin', 'org-users'] })
    } catch (err) {
      toast.error(apiErrorMessage(err, 'No se pudo reenviar la invitación.'))
    } finally {
      setBusyId(null)
    }
  }

  async function handleCancel(id: string): Promise<void> {
    setBusyId(id)
    try {
      await cancelOrgInvite(id)
      toast.ok('Invitación cancelada')
      await qc.invalidateQueries({ queryKey: ['admin', 'org-users'] })
    } catch (err) {
      toast.error(apiErrorMessage(err, 'No se pudo cancelar la invitación.'))
    } finally {
      setBusyId(null)
    }
  }

  const pending = data?.pending ?? []

  const inviteColumns: DataTableColumn<PendingInvite>[] = [
    { key: 'email', label: 'Correo', render: (i) => <span className="text-text">{i.email}</span> },
    {
      key: 'platform_role',
      label: 'Rol',
      render: (i) => (
        <Chip tone={ROLE_BADGE[i.platform_role].tone}>{ROLE_BADGE[i.platform_role].label}</Chip>
      ),
    },
    { key: 'expires_at', label: 'Vence', render: (i) => relativeDateEs(i.expires_at) },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: (i) => (
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            variant="secondary"
            disabled={busyId === i.id}
            onClick={() => void handleResend(i.id)}
          >
            Reenviar
          </Button>
          <Button
            size="sm"
            variant="destructive"
            disabled={busyId === i.id}
            onClick={() => void handleCancel(i.id)}
          >
            Cancelar
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div data-testid="admin-users" className="mx-auto w-full max-w-5xl">
      <AddUserModal
        open={addUserOpen}
        onClose={() => setAddUserOpen(false)}
        onSuccess={refreshMembers}
        roles={roles}
        isOwnerCaller={isOwnerCaller}
      />

      <PageHeader
        title="Usuarios"
        subtitle="El equipo INHERTA: roles, accesos y invitaciones."
        actions={<Button onClick={() => setAddUserOpen(true)}>Agregar usuario</Button>}
      />

      <div className="mt-4">
        <Tabs
          tabs={[
            { id: 'equipo', label: 'Equipo' },
            { id: 'directorio', label: 'Directorio' },
            { id: 'invitaciones', label: 'Invitaciones' },
            { id: 'seguridad', label: 'Seguridad' },
          ]}
          activeId={tab}
          onChange={(id) => setTab(id as typeof tab)}
          idBase="admin-users"
          aria-label="Secciones de usuarios"
        />
      </div>

      <div className="mt-5">
        <TabPanel idBase="admin-users" tabId={tab}>
          {tab === 'equipo' && <EquipoPanel navigate={(path) => void navigate(path)} />}
          {tab === 'directorio' && <DirectoryPanel />}
          {tab === 'invitaciones' && (
            <Card>
              <Card.Body>
                <h2 className="text-[12.5px] font-medium text-text-2">Invitaciones pendientes</h2>
                {pending.length === 0 ? (
                  <p className="mt-3 text-[12.5px] text-text-2">No hay invitaciones pendientes.</p>
                ) : (
                  <div className="mt-3">
                    <DataTable
                      caption="Invitaciones pendientes"
                      columns={inviteColumns}
                      rows={pending}
                    />
                  </div>
                )}
              </Card.Body>
            </Card>
          )}
          {tab === 'seguridad' && <SecurityCard />}
        </TabPanel>
      </div>
    </div>
  )
}
